import React, { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Moon as MoonIcon, Eye, EyeOff } from "lucide-react";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import Starfield from "@/components/Starfield";

function GoogleButton({ label }) {
  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  const go = () => {
    const redirectUrl = window.location.origin + "/dashboard";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };
  return (
    <button
      type="button"
      onClick={go}
      data-testid="google-auth-button"
      className="flex w-full items-center justify-center gap-3 rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-sm text-zinc-100 transition-colors hover:border-white/30"
    >
      <svg className="h-4 w-4" viewBox="0 0 24 24"><path fill="#fff" d="M21.35 11.1h-9.17v2.98h5.27c-.23 1.4-1.64 4.1-5.27 4.1-3.17 0-5.76-2.62-5.76-5.85s2.59-5.85 5.76-5.85c1.8 0 3.01.77 3.7 1.43l2.52-2.43C17.2 3.9 15.0 3 12.18 3 7.03 3 2.86 7.17 2.86 12.33s4.17 9.33 9.32 9.33c5.38 0 8.94-3.78 8.94-9.1 0-.61-.07-1.08-.17-1.46z"/></svg>
      {label}
    </button>
  );
}

export default function Login() {
  const navigate = useNavigate();
  const { setSession } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [show, setShow] = useState(false);
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const { data } = await api.post("/auth/login", { email, password });
      setSession(data.token, data.user);
      navigate("/dashboard", { replace: true });
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || "Login failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="relative flex min-h-screen items-center justify-center overflow-hidden bg-[#06080d] px-5 py-12 text-white">
      <Starfield count={70} />
      <div className="glass-strong relative z-10 w-full max-w-md overflow-hidden rounded-3xl p-8">
        <div className="pointer-events-none absolute -right-10 -top-10 h-32 w-32 rounded-full"
             style={{ background: "radial-gradient(circle, rgba(255,255,255,0.14), transparent 70%)" }} />
        <Link to="/" className="flex items-center gap-2.5" data-testid="login-logo">
          <MoonIcon className="h-6 w-6" strokeWidth={1.5} />
          <span className="font-sans text-lg font-semibold tracking-tight">Quizer</span>
        </Link>
        <h1 className="mt-8 font-sans text-3xl font-light tracking-tight">Welcome back.</h1>
        <p className="mt-2 text-sm text-zinc-400">Log in to continue under a smarter sky.</p>

        <form onSubmit={submit} className="mt-8 space-y-4">
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">Email</label>
            <input
              type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
              data-testid="login-email-input"
              className="mt-2 w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 text-sm text-white placeholder:text-zinc-500 focus:border-white/30 focus:outline-none"
              placeholder="you@example.com"
            />
          </div>
          <div>
            <label className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-400">Password</label>
            <div className="relative mt-2">
              <input
                type={show ? "text" : "password"} required value={password} onChange={(e) => setPassword(e.target.value)}
                data-testid="login-password-input"
                className="w-full rounded-xl border border-white/10 bg-white/5 px-4 py-3 pr-11 text-sm text-white placeholder:text-zinc-500 focus:border-white/30 focus:outline-none"
                placeholder="••••••••"
              />
              <button type="button" onClick={() => setShow((s) => !s)} data-testid="login-password-toggle" className="absolute right-3 top-1/2 -translate-y-1/2 text-zinc-400 hover:text-white">
                {show ? <EyeOff className="h-4 w-4" /> : <Eye className="h-4 w-4" />}
              </button>
            </div>
          </div>

          {error && <p className="text-sm text-red-400" data-testid="login-error">{error}</p>}

          <button type="submit" disabled={loading} data-testid="login-submit-button" className="shimmer w-full rounded-xl bg-white py-3 text-sm font-semibold text-[#06080d] transition-opacity disabled:opacity-50">
            {loading ? "Signing in…" : "Log In"}
          </button>
        </form>

        <div className="my-6 flex items-center gap-4">
          <span className="h-px flex-1 bg-white/10" />
          <span className="font-mono text-[10px] uppercase tracking-[0.2em] text-zinc-500">or</span>
          <span className="h-px flex-1 bg-white/10" />
        </div>

        <GoogleButton label="Continue with Google" />

        <p className="mt-6 text-center text-sm text-zinc-400">
          New to Quizer?{" "}
          <Link to="/register" data-testid="login-to-register" className="text-white underline-offset-4 hover:underline">Create an account</Link>
        </p>
      </div>
    </div>
  );
}

export { GoogleButton };
